"use client";

import { useEffect, useState } from "react";
import { Search, Heart, Bell, TrendingDown, ShoppingCart } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

const steps = [
  {
    icon: Search,
    title: "Choose a category",
    description:
      "Browse buying guides for your category and intended use.",
  },
  {
    icon: Heart,
    title: "Compare the shortlist",
    description:
      "Compare the exact models, features and prices recorded in the guide.",
  },
  {
    icon: Bell,
    title: "Read the trade-offs",
    description:
      "Consider limitations and whether a lower-ranked option suits your needs.",
  },
  {
    icon: TrendingDown,
    title: "Check the date",
    description:
      "Check when a price was recorded. Historical observations do not predict future offers.",
  },
  {
    icon: ShoppingCart,
    title: "Buy Confidently",
    description:
      "Confirm the model, warranty, stock and final price with the retailer.",
  },
];

const HowItWorksNavbar = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const isMobile = useIsMobile();

  useEffect(() => {
    // Only auto-scroll on mobile devices
    if (!isMobile) return;
    
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % steps.length);
    }, 3000); // auto-slide every 3s
    return () => clearInterval(interval);
  }, [isMobile]);

  return (
    <section className="py-8 bg-[hsl(10.1,67.8%,95%)] dark:bg-muted/30">
      <div className="container mx-auto px-4 text-center">
        <h2 className="text-3xl font-bold mb-4 text-[hsl(10.1,67.8%,50%)]">
          How to use our buying guides
        </h2>
        <p className="text-gray-600 dark:text-muted-foreground mb-8 max-w-2xl mx-auto">
          Choose a topic, compare alternatives and check the retailer’s current offer.
        </p>

        {/* Carousel Cards - Desktop shows all, Mobile scrolls */}
        <div className="relative overflow-hidden">
          <div
            className={`flex transition-transform duration-700 ease-in-out ${
              !isMobile ? 'transform-none' : ''
            }`}
            style={{ 
              transform: isMobile ? `translateX(-${activeIndex * 100}%)` : 'none'
            }}
          >
            {steps.map((step, index) => {
              const StepIcon = step.icon;
              return (
                <div 
                  key={index} 
                  className={`flex-shrink-0 px-2 ${
                    isMobile ? 'w-full' : 'w-1/5'
                  }`}
                >
                  <div className="bg-white dark:bg-card border border-[hsl(10.1,67.8%,50%)] rounded-xl shadow-lg p-4 h-64 flex flex-col justify-start items-center">
                    {/* Rectangular Step Number */}
                    <div className="flex items-center justify-center w-16 h-10 bg-[hsl(10.1,67.8%,50%)] text-white rounded-md mb-4 font-bold">
                      Step {index + 1}
                    </div>

                    {/* Icon */}
                    <div className="text-[hsl(10.1,67.8%,50%)] mb-4">
                      <StepIcon className="w-8 h-8 mx-auto" strokeWidth={1.5} />
                    </div>

                    {/* Title */}
                    <h3 className="font-semibold text-lg mb-2 text-foreground">{step.title}</h3>

                    {/* Description */}
                    <p className="text-sm text-gray-600 dark:text-muted-foreground text-center">{step.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Mobile indicators */}
        {isMobile && (
          <div className="flex justify-center gap-2 mt-4">
            {steps.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveIndex(index)}
                className={`w-2 h-2 rounded-full transition-colors ${
                  index === activeIndex 
                    ? 'bg-[hsl(10.1,67.8%,50%)]' 
                    : 'bg-[hsl(10.1,67.8%,80%)]'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default HowItWorksNavbar;